/**
 * Client-side helpers aligned with PayrollPeriodService (backend).
 */

export const PAYROLL_FREQUENCIES = ["monthly", "weekly", "fortnightly", "daily"];

export const FREQUENCY_WINDOW_DAYS = {
  weekly: 7,
  fortnightly: 14,
  daily: 1,
};

export function formatDateISO(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** YYYY-MM using the local calendar (never UTC via toISOString). */
export function toYYYYMM(val) {
  if (!val) return "";

  if (typeof val === "string") {
    const match = val.match(/^(\d{4}-\d{2})/);
    if (match) return match[1];
  }

  const date = val instanceof Date ? val : new Date(val);
  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

export function currentPayrollYYYYMM(date = new Date()) {
  return toYYYYMM(date);
}

export function currentPayrollMonthStart(date = new Date()) {
  const ym = currentPayrollYYYYMM(date);
  return ym ? `${ym}-01` : "";
}

/** Normalize API/form values to the first day of the payroll month (YYYY-MM-01). */
export function normalizePayrollMonthStart(val) {
  const ym = toYYYYMM(val);
  return ym ? `${ym}-01` : currentPayrollMonthStart();
}

/** Human label, e.g. "August 2026", without UTC timezone drift. */
export function formatPayrollMonthLabel(val) {
  const ym = toYYYYMM(val);
  if (!ym) return "-";

  const [year, month] = ym.split("-").map(Number);
  const d = new Date(year, month - 1, 1);
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

export function parseDateISO(value) {
  if (!value || typeof value !== "string") return null;
  const parts = value.split("-").map(Number);
  if (parts.length !== 3) return null;
  const d = new Date(parts[0], parts[1] - 1, parts[2]);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function addDaysISO(isoDate, days) {
  const d = parseDateISO(isoDate);
  if (!d) return "";
  d.setDate(d.getDate() + days);
  return formatDateISO(d);
}

export function countInclusiveDays(start, end) {
  const s = parseDateISO(start);
  const e = parseDateISO(end);
  if (!s || !e) return 0;
  const diff = Math.round((e - s) / (24 * 60 * 60 * 1000));
  return diff + 1;
}

/** Monday of the week containing `anchor` (local calendar). */
export function mondayOfWeek(anchor = new Date()) {
  const d = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate());
  const day = d.getDay();
  const offset = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + offset);
  return d;
}

export function endDateForFrequency(frequency, startIso) {
  const days = FREQUENCY_WINDOW_DAYS[frequency];
  if (!days || !startIso) return "";
  if (frequency === "daily") return startIso;
  return addDaysISO(startIso, days - 1);
}

export function buildPeriodCodePreview(frequency, periodStart, periodEnd) {
  if (!periodStart) return "";

  if (frequency === "monthly") {
    return String(periodStart).slice(0, 7);
  }

  if (frequency === "daily") {
    return `daily:${periodStart}`;
  }

  if (!periodEnd) return "";

  if (frequency === "weekly") {
    return `weekly:${periodStart}:${periodEnd}`;
  }

  if (frequency === "fortnightly") {
    return `fortnightly:${periodStart}:${periodEnd}`;
  }

  return "";
}

export function validateFrequencyWindow(frequency, periodStart, periodEnd) {
  if (frequency === "monthly") {
    return { ok: true, message: "" };
  }

  if (!periodStart || !periodEnd) {
    return { ok: false, message: "Start and end dates are required." };
  }

  if (frequency === "daily") {
    if (periodStart !== periodEnd) {
      return { ok: false, message: "Daily payroll requires the same start and end date." };
    }
    return { ok: true, message: "" };
  }

  const expected = FREQUENCY_WINDOW_DAYS[frequency];
  const actual = countInclusiveDays(periodStart, periodEnd);

  if (actual !== expected) {
    return {
      ok: false,
      message: `${frequency.charAt(0).toUpperCase() + frequency.slice(1)} payroll requires exactly ${expected} days (${actual} selected).`,
    };
  }

  if (parseDateISO(periodEnd) < parseDateISO(periodStart)) {
    return { ok: false, message: "End date must be on or after the start date." };
  }

  return { ok: true, message: "" };
}

/**
 * Suggest a default payroll window from an anchor date.
 */
export function suggestPeriodWindow(frequency, anchor = new Date()) {
  if (frequency === "monthly") {
    return {
      period: formatDateISO(anchor).slice(0, 7),
      periodStart: "",
      periodEnd: "",
    };
  }

  if (frequency === "daily") {
    const day = formatDateISO(anchor);
    return { period: day.slice(0, 7), periodStart: day, periodEnd: day };
  }

  const start = formatDateISO(mondayOfWeek(anchor));
  const end = endDateForFrequency(frequency, start);

  return {
    period: start.slice(0, 7),
    periodStart: start,
    periodEnd: end,
  };
}

/** Shift the current window one period backward or forward. */
export function shiftPeriodWindow(frequency, periodStart, periodEnd, direction = -1) {
  if (frequency === "monthly" || !periodStart) {
    return { periodStart, periodEnd };
  }

  const step =
    frequency === "daily" ? 1 : frequency === "weekly" ? 7 : 14;
  const delta = step * direction;

  const newStart = addDaysISO(periodStart, delta);
  const newEnd = endDateForFrequency(frequency, newStart);

  return { periodStart: newStart, periodEnd: newEnd };
}
